/**
 * JMC クリニック・店舗管理.
 *
 * What this screen was: two flat tables of identity - name, plan, two statuses, a
 * count, a date - with no search, no filter and no sort. It answered "does this
 * tenant exist", which is the one question nobody has, and it made the stores tab
 * a sibling of the clinics tab rather than what a store actually is: something
 * inside a clinic.
 *
 * What it is now follows how account lists work everywhere this problem has been
 * solved already - a customer list in Stripe, accounts in AWS Organizations,
 * projects in Vercel:
 *
 *   A toolbar, not a bare table. Search, status chips that carry the count of
 *   what is behind them, plan filter, sort. The chips are the navigation: an
 *   operator does not scan 80 rows for the suspended one, they press 停止中 3.
 *
 *   One value per cell. The first version stacked a figure and a muted caption
 *   in most columns - 2 店舗 over 管理者 1・利用者 1, a spend over its cap over a
 *   meter - so no two cells lined up and the eye could not run down a column.
 *   Every number now has a column of its own, and the row that needs attention
 *   still says why: no plan, no store, nobody assigned, over its allowance, or
 *   nothing written for a month.
 *
 *   No cap beside the spend. The plan screen is where a plan's numbers are set
 *   and read; repeating "上限 $20" on every row measured the clinic against a
 *   figure it cannot act on and made the column two lines tall for it.
 *
 *   The hierarchy in place. A clinic row expands to its own stores, from the
 *   store list already loaded, so "what does this clinic actually have" is one
 *   click and not a tab switch plus a search.
 *
 * The store tab stays, because there is a real question it alone answers: find a
 * store when the clinic it belongs to is what you do NOT know.
 *
 * Article counts are absent on purpose, and not for lack of a column: a platform
 * session cannot read the articles table at all (migration 013). `articles_used`
 * from the usage ledger is the same activity counted somewhere JMC may look.
 *
 * Ten rows at a time, ordered by the server. Sorting in the browser was right
 * while every row was already here; with a page of ten it would sort the ten
 * that happen to be loaded, which is a different question from sorting the
 * tenants and showing the first ten.
 */
import { useEffect, useState } from 'react';
import { Link, useLoaderData, useSearchParams } from 'react-router';
import type { AdminClinic, AdminClinicList, AdminStore, ListResponse } from '../api.ts';
import { cachedGet } from '../cache.ts';
import { usePaged } from '../paging.ts';
import {
  Card,
  Empty,
  Pill,
  RowLink,
  StatusPill,
  Table,
  Tabs,
  jaDate,
  jaNumber,
} from '../components/ui.tsx';
import { Select } from '../components/Select.tsx';

interface LoaderData {
  clinics: AdminClinicList;
  stores: ListResponse<AdminStore>;
}

type TabId = 'clinics' | 'stores';

/** Micro-USD as money. Same rule as the dashboard: integers until the last step. */
const usd = (micro: number | string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: Number(micro) < 10_000_000 ? 2 : 0,
  }).format(Number(micro) / 1_000_000);

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '有効',
  SUSPENDED: '停止中',
  ARCHIVED: 'アーカイブ',
};

/** A clinic with no activity for this long is a clinic nobody is using. */
const DORMANT_DAYS = 30;

/** Rows per request, first page included. */
const PAGE = 10;

/*
 * The tables behind each list, for the stale-while-revalidate cache. Module
 * constants rather than inline literals: a new array on every render would look
 * to `usePaged` like a new list and reset the pages it had loaded.
 */
const CLINIC_TABLES = ['clinics', 'subscriptions'] as const;
const STORE_TABLES = ['stores', 'clinics'] as const;

const daysSince = (iso: string | null): number | null =>
  iso === null ? null : Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

/**
 * Why this row needs somebody, in the order it needs them.
 *
 * Derived here rather than stored, like the dashboard's follow-up list: every
 * one of these is a condition that is either true right now or is not, and a
 * flag somebody has to clear by hand is a flag that goes stale.
 */
function attention(clinic: AdminClinic): { label: string; tone: 'danger' | 'warn' }[] {
  const flags: { label: string; tone: 'danger' | 'warn' }[] = [];
  if (clinic.status !== 'ACTIVE') {
    flags.push({ label: STATUS_LABEL[clinic.status] ?? clinic.status, tone: 'danger' });
  }
  if (!clinic.plan_id) flags.push({ label: 'プラン未設定', tone: 'danger' });
  if (Number(clinic.store_count) === 0) flags.push({ label: '店舗なし', tone: 'warn' });
  if (Number(clinic.admin_count) === 0) flags.push({ label: '管理者なし', tone: 'warn' });

  const spendLimit = clinic.ai_micro_cost_limit === null ? null : Number(clinic.ai_micro_cost_limit);
  if (spendLimit !== null && spendLimit > 0) {
    const ratio = Number(clinic.ai_micro_cost_used) / spendLimit;
    if (ratio >= 1) flags.push({ label: 'AI枠を使い切り', tone: 'danger' });
    else if (ratio >= 0.8) flags.push({ label: 'AI枠が残り少', tone: 'warn' });
  }

  const idle = daysSince(clinic.last_article_at);
  if (clinic.status === 'ACTIVE' && Number(clinic.store_count) > 0 && (idle === null || idle >= DORMANT_DAYS)) {
    flags.push({ label: idle === null ? '未利用' : `${DORMANT_DAYS}日以上未更新`, tone: 'warn' });
  }
  return flags;
}

/*
 * The sort is the server's now - see CLINIC_ORDER in the admin routes. A list
 * that arrives ten rows at a time cannot be ordered here: sorting the page
 * would reorder ten arbitrary clinics rather than choosing which ten.
 */
type ClinicSort = 'new' | 'name' | 'spend' | 'idle';

/**
 * The stores of one clinic, fetched when the row is opened.
 *
 * They used to be sliced out of the store list this screen already held, which
 * worked only while that list was every store there is. It is a page of ten
 * now, so the rows are asked for by clinic — through the same cache as
 * everything else, so opening a row a second time costs no request.
 */
function ClinicStores({ clinicId }: { clinicId: string }) {
  const [stores, setStores] = useState<AdminStore[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    void cachedGet<ListResponse<AdminStore>>(
      `/admin/stores?clinicId=${clinicId}&limit=100`,
      STORE_TABLES,
    )
      .then((page) => live && setStores(page.items))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, [clinicId]);

  if (failed) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        店舗を読み込めませんでした。
      </p>
    );
  }
  if (stores === null) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        読み込み中…
      </p>
    );
  }
  if (stores.length === 0) {
    return (
      <p className="muted small" style={{ margin: 0 }}>
        店舗が登録されていません。「開く」からこのクリニックに店舗を追加できます。
      </p>
    );
  }
  return (
    <ul className="tenantstores">
      {stores.map((store) => (
        <li key={store.id}>
          <Link to={`/admin/stores/${store.id}`}>{store.name}</Link>
          <StatusPill status={store.status} />
          <span className="muted small">
            利用者 {jaNumber(store.member_count)}・登録 {jaDate(store.created_at)}
          </span>
        </li>
      ))}
    </ul>
  );
}

/** The columns, so the row and its expanded panel cannot disagree on how many. */
const CLINIC_COLUMNS = 11;

/**
 * One clinic, and its stores underneath when opened.
 *
 * Two `<tr>` rather than a nested table: the store rows keep the parent's column
 * grid, so the store name sits under the clinic name instead of starting a
 * second layout the eye has to re-learn.
 *
 * No `row-link` here. That class stretches one anchor over the whole row, which
 * cannot coexist with a toggle - so the row's own control is the toggle, and the
 * link out is explicit.
 */
function ClinicRow({ clinic }: { clinic: AdminClinic }) {
  const [open, setOpen] = useState(false);
  const flags = attention(clinic);

  return (
    <>
      <tr className={open ? 'tenant--open' : undefined}>
        <td>
          <button
            type="button"
            className="tenant__toggle"
            aria-expanded={open}
            onClick={() => setOpen(!open)}
          >
            <span className="tenant__caret" aria-hidden>
              {open ? '▾' : '▸'}
            </span>
            <span className="tenant__name">{clinic.name}</span>
          </button>
        </td>

        <td>{jaDate(clinic.created_at)}</td>

        <td>{clinic.plan_id ? <Pill>{clinic.plan_id}</Pill> : <Pill tone="warn">未設定</Pill>}</td>

        <td>
          {clinic.subscription_status ? (
            <StatusPill status={clinic.subscription_status} />
          ) : (
            <span className="muted">—</span>
          )}
        </td>

        <td className="num">{jaNumber(clinic.store_count)}</td>
        <td className="num">{jaNumber(clinic.admin_count)}</td>
        <td className="num">{jaNumber(clinic.member_count)}</td>
        <td className="num">{jaNumber(clinic.articles_used)}</td>
        <td className="num">{jaNumber(clinic.images_used)}</td>

        {/* Measured spend, from the usage the provider reported per call. No
            cap beside it: the plan is where a limit is set and read. */}
        <td className="num">{usd(clinic.ai_micro_cost_used)}</td>

        <td>
          {flags.length === 0 ? (
            <Pill tone="ok">問題なし</Pill>
          ) : (
            <span className="dashcounts">
              {flags.map((flag) => (
                <Pill key={flag.label} tone={flag.tone}>
                  {flag.label}
                </Pill>
              ))}
            </span>
          )}
          <Link className="btn btn--ghost btn--sm tenant__open" to={`/admin/clinics/${clinic.id}`}>
            開く
          </Link>
        </td>
      </tr>

      {open && (
        <tr className="tenant__stores">
          <td colSpan={CLINIC_COLUMNS}>
            <ClinicStores clinicId={clinic.id} />
          </td>
        </tr>
      )}
    </>
  );
}

function ClinicTable({ items, total }: { items: AdminClinic[]; total: number }) {
  if (items.length === 0) return <Empty>条件に合うクリニックがありません。</Empty>;
  return (
    <Table
      caption={`${jaNumber(total)} 件中 ${jaNumber(items.length)} 件を表示しています。行の名前を選ぶと、その店舗が開きます。`}
      head={
        <>
          <th scope="col">クリニック</th>
          <th scope="col">登録日</th>
          <th scope="col">プラン</th>
          <th scope="col">契約</th>
          <th scope="col" className="num">
            店舗
          </th>
          <th scope="col" className="num">
            管理者
          </th>
          <th scope="col" className="num">
            利用者
          </th>
          <th scope="col" className="num">
            記事
          </th>
          <th scope="col" className="num">
            画像
          </th>
          <th scope="col" className="num">
            AI利用額
          </th>
          <th scope="col">状態</th>
        </>
      }
    >
      {items.map((clinic) => (
        <ClinicRow key={clinic.id} clinic={clinic} />
      ))}
    </Table>
  );
}

function StoreTable({ items }: { items: AdminStore[] }) {
  if (items.length === 0) return <Empty>条件に合う店舗がありません。</Empty>;
  return (
    <Table
      caption={`${jaNumber(items.length)} 件を表示しています。`}
      head={
        <>
          <th scope="col">店舗名</th>
          <th scope="col">クリニック</th>
          <th scope="col" className="num">
            利用者
          </th>
          <th scope="col">状態</th>
          <th scope="col">登録日</th>
        </>
      }
    >
      {items.map((store) => (
        <tr key={store.id} className="row-link">
          <td>
            <RowLink to={`/admin/stores/${store.id}`}>{store.name}</RowLink>
          </td>
          <td className="muted">
            {store.clinic_name}
            {store.clinic_status !== 'ACTIVE' && (
              <>
                {' '}
                {/* The parent's state decides whether this store can be used at
                    all, so it is not something to discover one screen deeper. */}
                <Pill tone="danger">
                  クリニック{STATUS_LABEL[store.clinic_status] ?? store.clinic_status}
                </Pill>
              </>
            )}
          </td>
          <td className="num">{jaNumber(store.member_count)}</td>
          <td>
            <StatusPill status={store.status} />
          </td>
          <td>{jaDate(store.created_at)}</td>
        </tr>
      ))}
    </Table>
  );
}

export default function AdminTenants() {
  const { clinics, stores } = useLoaderData<LoaderData>();
  // The tab lives in the URL, so "すべて見る" can land on the right one and a
  // shared link opens what the sender was looking at. So do the filters.
  const [params, setParams] = useSearchParams();
  const tab: TabId = params.get('tab') === 'stores' ? 'stores' : 'clinics';
  const [query, setQuery] = useState(params.get('q') ?? '');
  // In the URL, because it decides which rows the server sends rather than how
  // the ones already here are arranged.
  const sort = (params.get('sort') ?? 'new') as ClinicSort;

  /** One parameter changed, everything else kept. Empty value removes the key. */
  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  const status = params.get('status') ?? '';
  const plan = params.get('plan') ?? '';

  const facet = (kind: string, key: string) =>
    clinics.facets.find((row) => row.kind === kind && row.key === key)?.count ?? 0;
  const statusTotal = clinics.facets
    .filter((row) => row.kind === 'status')
    .reduce((sum, row) => sum + Number(row.count), 0);

  /*
   * The pages after the first, for whichever tab is open. Both go through the
   * change-stream cache, so scrolling back to a page already seen costs no
   * request and a row that changes still invalidates the page holding it.
   *
   * The query string is carried into the paged URL: page two of a filtered list
   * has to be filtered the same way, or the rows that arrive are not the rows
   * being looked at.
   */
  const filters = new URLSearchParams();
  for (const key of ['q', 'status', 'plan'] as const) {
    const value = params.get(key);
    if (value) filters.set(key, value);
  }
  const clinicQuery = new URLSearchParams(filters);
  clinicQuery.set('sort', sort);
  const storeQuery = new URLSearchParams();
  for (const key of ['q', 'status'] as const) {
    const value = params.get(key);
    if (value) storeQuery.set(key, value);
  }

  const pagedClinics = usePaged<AdminClinic>(
    `/admin/clinics?${clinicQuery.toString()}`,
    clinics.items,
    PAGE,
    true,
    CLINIC_TABLES,
  );
  const pagedStores = usePaged<AdminStore>(
    `/admin/stores?${storeQuery.toString()}`,
    stores.items,
    PAGE,
    true,
    STORE_TABLES,
  );
  const paged = tab === 'clinics' ? pagedClinics : pagedStores;
  const total = (tab === 'clinics' ? clinics.total : stores.total) ?? paged.items.length;

  const attentionCount = clinics.items.filter((clinic) => attention(clinic).length > 0).length;

  return (
    <Card
      title="クリニック・店舗管理"
      caption={
        tab === 'clinics'
          ? 'クリニック名を選ぶと、その店舗が展開されます。「状態」列は登録内容から都度算出しています。'
          : 'クリニックが分からない店舗を探すための一覧です。店舗名でもクリニック名でも検索できます。'
      }
      actions={
        <Link className="btn btn--sm" to="/admin/clinics/new">
          ＋ クリニックを登録
        </Link>
      }
    >
      <Tabs<TabId>
        tabs={[
          { id: 'clinics', label: `クリニック一覧（${clinics.total}）` },
          { id: 'stores', label: `店舗一覧（${stores.total ?? stores.items.length}）` },
        ]}
        active={tab}
        onChange={(next) => {
          // Filters belong to the tab that was typed into: carrying a clinic
          // search across to the store list returns a different, confusing set.
          setQuery('');
          setParams(next === 'stores' ? { tab: 'stores' } : {});
        }}
      />

      <form
        className="tenantbar"
        onSubmit={(event) => {
          event.preventDefault();
          setParam('q', query.trim());
        }}
      >
        <div className="field" style={{ marginBottom: 0, minWidth: 220 }}>
          <label htmlFor="q">検索</label>
          <input
            id="q"
            value={query}
            placeholder={tab === 'clinics' ? 'クリニック名' : '店舗名またはクリニック名'}
            maxLength={200}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>

        {tab === 'clinics' && (
          <>
            <div className="field" style={{ marginBottom: 0, minWidth: 150 }}>
              <label htmlFor="plan">プラン</label>
              <Select
                id="plan"
                value={plan}
                onChange={(value) => setParam('plan', value)}
                options={[
                  { value: '', label: 'すべて' },
                  ...clinics.facets
                    .filter((row) => row.kind === 'plan' && row.key !== '')
                    .map((row) => ({ value: row.key, label: `${row.key}（${row.count}）` })),
                ]}
              />
            </div>
            <div className="field" style={{ marginBottom: 0, minWidth: 170 }}>
              <label htmlFor="sort">並び順</label>
              <Select
                id="sort"
                value={sort}
                onChange={(value) => setParam('sort', value === 'new' ? '' : value)}
                options={[
                  { value: 'new', label: '登録が新しい順' },
                  { value: 'name', label: 'クリニック名順' },
                  { value: 'spend', label: 'AI利用額が多い順' },
                  { value: 'idle', label: '更新が古い順' },
                ]}
              />
            </div>
          </>
        )}

        <button className="btn btn--sm" type="submit" style={{ alignSelf: 'end' }}>
          検索
        </button>
      </form>

      {/*
        The status filter as chips rather than a dropdown. Counts on the chip are
        the reason: 停止中 3 is both the filter and the answer to "is anything
        suspended", which a closed <select> can never be. Clinic counts come from
        the server over every clinic, not over the filtered page.
      */}
      <div className="chips tenantchips">
        <button
          type="button"
          className="chip"
          aria-pressed={status === ''}
          onClick={() => setParam('status', '')}
        >
          すべて{tab === 'clinics' && statusTotal > 0 ? `（${statusTotal}）` : ''}
        </button>
        {(['ACTIVE', 'SUSPENDED', 'ARCHIVED'] as const).map((key) => (
          <button
            key={key}
            type="button"
            className="chip"
            aria-pressed={status === key}
            onClick={() => setParam('status', key)}
          >
            {STATUS_LABEL[key]}
            {tab === 'clinics' ? `（${facet('status', key)}）` : ''}
          </button>
        ))}
        {tab === 'clinics' && attentionCount > 0 && (
          <span className="muted small" style={{ alignSelf: 'center' }}>
            うち {jaNumber(attentionCount)} 件に対応が必要な状態があります
          </span>
        )}
      </div>

      {/*
        The key swaps the panel on tab or filter change and the new table fades in
        over its own height. No exit: emptying the card first makes it collapse to
        the tab strip and spring back, which is the flicker.

        Opacity only, no transform: this wraps a table, and a transform on an
        ancestor reflows the columns mid-animation and the rows jitter.
      */}
      <div
        key={`${tab}-${status}-${plan}-${sort}-${params.get('q') ?? ''}`}
        className="animate-fade-in"
      >
        {tab === 'clinics' ? (
          <ClinicTable items={pagedClinics.items} total={clinics.total} />
        ) : (
          <StoreTable items={pagedStores.items} />
        )}
      </div>

      {/*
        The foot of the list: the observer's target, and the press that does the
        same thing for a reader who is not scrolling with a mouse.
      */}
      {!paged.done && paged.items.length > 0 && (
        <div ref={paged.sentinel} className="more-rows">
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            onClick={paged.more}
            disabled={paged.loading}
          >
            {paged.loading
              ? '読み込み中…'
              : `さらに表示（残り${jaNumber(Math.max(0, total - paged.items.length))}件）`}
          </button>
        </div>
      )}
      {paged.error && (
        <p className="alert" role="alert">
          続きを読み込めませんでした。画面を再読み込みしてください。
        </p>
      )}
    </Card>
  );
}
